import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { AgentRunDetail as Detail, AgentRunStep, AgentEmailDraft } from '../../types/agent';
import { TERMINAL_RUN_STATUSES } from '../../types/agent';
import { agentApi, streamRun, apiErrorMessage } from '../../services/agentApi';
import { RunStatusChip, fmtDateTime, fmtDuration, fmtTokens, btn, card, Spinner, EmailStatusChip } from './agentUi';
import RunTimeline from './RunTimeline';
import ArtifactPanel from './ArtifactPanel';
import ApprovalCard from './ApprovalCard';
import { useAuth } from '../../contexts/AuthContext';
import { useQuickStart } from '../../contexts/QuickStartContext';
import { useQuickStartAssistant } from '../../contexts/QuickStartAssistantContext';

interface Props {
  runId: string;
  onBack: () => void;
  highlightArtifactId?: string | null;
}

const AgentRunDetail: React.FC<Props> = ({ runId, onBack, highlightArtifactId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openQuickStart } = useQuickStart();
  const { switchConversation } = useQuickStartAssistant();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const lastSeq = useRef(0);

  const load = useCallback(async () => {
    try {
      const d = await agentApi.run(runId);
      setDetail(d);
      lastSeq.current = d.steps.length ? d.steps[d.steps.length - 1].seq : 0;
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the run'));
    }
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  const live = !!detail && !TERMINAL_RUN_STATUSES.includes(detail.run.status);

  // Live updates: SSE stream; a full reload when the run changes state so
  // artifacts/approvals/drafts refresh too. Polls as a fallback.
  useEffect(() => {
    if (!live) return;
    let lastStatus = detail?.run.status;
    const stop = streamRun(runId, lastSeq.current, (event) => {
      if (event.type === 'step') {
        const step = event.step as AgentRunStep;
        if (step.seq <= lastSeq.current) return;
        lastSeq.current = step.seq;
        setDetail((d) => (d ? { ...d, steps: [...d.steps, step] } : d));
        if (step.kind === 'artifact' || step.kind === 'approval_requested' || step.kind === 'approval_resolved') load();
      } else if (event.type === 'run') {
        if (event.run.status && event.run.status !== lastStatus) {
          lastStatus = event.run.status;
          load();
        }
      } else if (event.type === 'end') {
        load();
      }
    });
    const poll = setInterval(load, 20000);
    return () => {
      stop();
      clearInterval(poll);
    };
  }, [live, runId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div>
        <button className="text-sm text-primary hover:underline mb-4" onClick={onBack}>← All runs</button>
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
        <Spinner /> Loading run…
      </div>
    );
  }

  const { run, steps, artifacts, approvals, drafts, inboundEmail, owner } = detail;
  const isOwner = run.userId === user?.id;
  const pending = approvals.filter((a) => a.status === 'pending');
  const decided = approvals.filter((a) => a.status !== 'pending');
  const duration = run.finishedAt && run.startedAt ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime() : null;

  const onDraftChanged = (draft: AgentEmailDraft) => setDetail((d) => (d ? { ...d, drafts: d.drafts.map((x) => (x.id === draft.id ? draft : x)) } : d));

  const cancel = async () => {
    setCancelling(true);
    try {
      await agentApi.cancel(run.id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not cancel'));
    } finally {
      setCancelling(false);
    }
  };

  const openInArie = async () => {
    if (!run.conversationId) return;
    await switchConversation(run.conversationId);
    openQuickStart();
  };

  return (
    <div>
      <button className="text-sm text-primary hover:underline mb-4" onClick={onBack}>← All runs</button>

      <div className={`${card} p-4 mb-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <RunStatusChip status={run.status} mode={run.mode} />
              <span className="text-xs text-gray-500">{run.triggerType === 'email' ? '📧 Email' : run.triggerType === 'manual' ? '🤖 Manual' : 'Task'}</span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-1 break-words">
              {inboundEmail?.subject || run.instructions?.slice(0, 120) || 'Agent run'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {inboundEmail ? `From ${inboundEmail.fromName || inboundEmail.fromAddress} · ` : ''}
              Started {fmtDateTime(run.startedAt || run.createdAt)}
              {duration ? ` · ${fmtDuration(duration)}` : ''}
              {owner && !isOwner ? ` · for ${owner.firstName || ''} ${owner.lastName || ''}`.trimEnd() : ''}
              {run.tokenUsage?.tokensIn ? ` · ${fmtTokens(run.tokenUsage.tokensIn)} in / ${fmtTokens(run.tokenUsage.tokensOut)} out tokens` : ''}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {run.customerId && (
              <button className={btn.secondary} onClick={() => navigate(run.projectId ? `/customers/${run.customerId}/projects/${run.projectId}` : `/customers/${run.customerId}`)}>
                Open folder
              </button>
            )}
            {isOwner && run.conversationId && <button className={btn.secondary} onClick={openInArie}>Open in Arie</button>}
            {live && (isOwner || user?.role === 'admin' || user?.role === 'system_admin') && (
              <button className={btn.danger} onClick={cancel} disabled={cancelling}>{cancelling ? 'Cancelling…' : 'Cancel run'}</button>
            )}
          </div>
        </div>

        {run.mode === 'shadow' && (
          <div className="mt-3 text-xs rounded-md bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 px-3 py-2">
            Shadow mode — Arie planned this work but nothing was executed. Each proposed action is listed under Approvals; approving one runs it now.
          </div>
        )}
        {run.summary && (
          <div className="mt-3 text-sm text-gray-800 dark:text-gray-100">
            <ReactMarkdown>{run.summary}</ReactMarkdown>
          </div>
        )}
        {run.nextSteps?.length > 0 && (
          <ul className="mt-2 list-disc ml-5 text-sm text-gray-700 dark:text-gray-200">
            {run.nextSteps.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        )}
        {run.summaryCcAddresses && run.summaryCcAddresses.length > 0 && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Summary copied to: {run.summaryCcAddresses.join(', ')}</p>
        )}
        {run.error && run.status !== 'completed' && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{run.error}</p>}
      </div>

      {pending.length > 0 && (
        <div className="mb-4 space-y-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Waiting for approval</h3>
          {pending.map((a) => (
            <ApprovalCard key={a.id} approval={a} canDecide={isOwner || user?.role === 'admin' || user?.role === 'system_admin'} onResolved={load} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${card} p-4 lg:col-span-2`}>
          <RunTimeline steps={steps} live={live && run.status !== 'awaiting_approval'} />
        </div>
        <div className="space-y-4">
          <div className={`${card} p-4`}>
            <ArtifactPanel artifacts={artifacts} drafts={drafts} canEdit={isOwner} onDraftChanged={onDraftChanged} highlightId={highlightArtifactId} />
          </div>
          {inboundEmail && (
            <div className={`${card} p-4 text-sm`}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Email</h3>
                <EmailStatusChip status={inboundEmail.status} />
              </div>
              <p className="text-xs text-gray-500 mt-1">{inboundEmail.fromAddress} · {fmtDateTime(inboundEmail.receivedAt)}</p>
              {(inboundEmail.attachments || []).length > 0 && (
                <ul className="mt-2 text-xs space-y-0.5">
                  {(inboundEmail.attachments || []).map((a, i) => (
                    <li key={i} className={a.documentId ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 line-through'} title={a.skipped || ''}>
                      📎 {a.filename}{a.fromForwarded ? ' (forwarded)' : ''}{!a.documentId && a.skipped ? ` — ${a.skipped}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {decided.length > 0 && (
            <div className={`${card} p-4`}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Decisions</h3>
              <ul className="space-y-1 text-xs">
                {decided.map((a) => (
                  <li key={a.id} className="text-gray-700 dark:text-gray-200">
                    <span className={a.status === 'approved' ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}>{a.status}</span> — {a.action.displayName || a.action.tool}
                    {a.resolutionNote ? <span className="text-gray-500"> ({a.resolutionNote})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentRunDetail;
